import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useVendors } from '../vendors/VendorListPage'
import { useDemoRole } from '../../auth/useMembership'
import {
  Button,
  Currency,
  ErrorBanner,
  Field,
  FormattedDate,
  Input,
  Select,
  Tag,
  Textarea,
} from '../../components/ui'
import {
  useLifecycleActions,
  type BillLineItemInput,
  type ReceiptItemInput,
} from './useLifecycleActions'

export type LifecycleItem = {
  id: string
  material_id: string
  material_name: string
  material_unit: string
  quantity: number
  unit: string
  status: string
  lifecycle_status: string
}

export type LifecycleReceipt = {
  id: string
  material_request_id: string
  quantity_received: number
  received_at: number
  challan_number: string | null
}

export type LifecycleBill = {
  id: string
  material_request_id: string
  bill_number: string
  bill_date: number
  total_amount: number
  paid_amount: number
  status: 'unpaid' | 'partial' | 'paid'
}

export type LifecycleBatch = {
  batch_id: string
  project_id: string
  status: string
  vendor_id: string | null
  vendor_name: string | null
  category: string | null
  items: LifecycleItem[]
  receipts: LifecycleReceipt[]
  bills: LifecycleBill[]
}

const MANAGER_ROLES = new Set(['manager', 'admin', 'super_admin'])
const SITE_ROLES = new Set(['site_engineer', 'manager', 'admin', 'super_admin'])

export function MaterialRequestLifecyclePanel({ batch }: { batch: LifecycleBatch }) {
  const role = useDemoRole().data?.role ?? null
  const vendors = useVendors()
  const actions = useLifecycleActions(batch.batch_id, batch.project_id, batch.vendor_id)

  const billedRequestIds = useMemo(
    () => new Set(batch.bills.map((b) => b.material_request_id)),
    [batch.bills],
  )
  const allItemsBilled =
    batch.items.length > 0 && batch.items.every((i) => billedRequestIds.has(i.id))

  const canManage = role ? MANAGER_ROLES.has(role) : true
  const canReceive = role ? SITE_ROLES.has(role) : true

  return (
    <div className="mt-3 border-t border-surface-700/60 pt-4 space-y-5">
      <ItemsSection items={batch.items} />
      {batch.receipts.length > 0 && <ReceiptsSection receipts={batch.receipts} />}
      {batch.bills.length > 0 && <BillsSection bills={batch.bills} />}

      {batch.status === 'requested' && canManage && (
        <ApproveRejectSection
          onApprove={() => actions.approve.mutate()}
          approving={actions.approve.isPending}
          approveError={actions.approve.error}
          onReject={(reason) => actions.reject.mutate(reason)}
          rejecting={actions.reject.isPending}
          rejectError={actions.reject.error}
        />
      )}

      {(batch.status === 'approved' || batch.status === 'partially_received') && canReceive && (
        <RecordReceiptSection
          batch={batch}
          vendors={vendors.data ?? []}
          submitting={actions.recordReceipt.isPending}
          error={actions.recordReceipt.error}
          onSubmit={(input) => actions.recordReceipt.mutate(input)}
        />
      )}

      {batch.status === 'received' && !allItemsBilled && canReceive && (
        <CreateBillSection
          batch={batch}
          vendors={vendors.data ?? []}
          submitting={actions.createBill.isPending}
          error={actions.createBill.error}
          onSubmit={(input) => actions.createBill.mutate(input)}
        />
      )}

      {(batch.status === 'billed' || batch.status === 'partial_paid') && batch.vendor_id && (
        <PaySection vendorId={batch.vendor_id} vendorName={batch.vendor_name} />
      )}

      {batch.status === 'paid' && (
        <p className="text-sm text-emerald-300">Settled ✓</p>
      )}

      {batch.status === 'rejected' && (
        <p className="text-sm text-rose-300">Rejected.</p>
      )}
    </div>
  )
}

function ItemsSection({ items }: { items: LifecycleItem[] }) {
  return (
    <section>
      <h4 className="text-xs uppercase tracking-wide text-surface-400 mb-2">Items</h4>
      <ul className="divide-y divide-surface-800 text-sm">
        {items.map((it) => (
          <li key={it.id} className="py-2 flex justify-between gap-3">
            <div>
              <div>{it.material_name}</div>
              <div className="text-xs text-surface-400">
                {it.quantity} {it.unit || it.material_unit}
              </div>
            </div>
            <div className="text-right space-x-1">
              <Tag tone={statusTone(it.status)}>{it.status}</Tag>
              {it.lifecycle_status && it.lifecycle_status !== it.status && (
                <Tag>{it.lifecycle_status}</Tag>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

function ReceiptsSection({ receipts }: { receipts: LifecycleReceipt[] }) {
  return (
    <section>
      <h4 className="text-xs uppercase tracking-wide text-surface-400 mb-2">Receipts</h4>
      <ul className="divide-y divide-surface-800 text-sm">
        {receipts.map((r) => (
          <li key={r.id} className="py-2 flex justify-between gap-3">
            <div>
              <div>Challan {r.challan_number ?? '—'}</div>
              <div className="text-xs text-surface-400">
                <FormattedDate ts={r.received_at} />
              </div>
            </div>
            <div className="text-right text-xs text-surface-300">
              qty {r.quantity_received}
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

function BillsSection({ bills }: { bills: LifecycleBill[] }) {
  return (
    <section>
      <h4 className="text-xs uppercase tracking-wide text-surface-400 mb-2">Bills</h4>
      <ul className="divide-y divide-surface-800 text-sm">
        {bills.map((b) => (
          <li key={b.id} className="py-2 flex justify-between gap-3">
            <div>
              <div>{b.bill_number}</div>
              <div className="text-xs text-surface-400">
                <FormattedDate ts={b.bill_date} />
              </div>
            </div>
            <div className="text-right">
              <Currency amount={b.total_amount} />
              <div className="text-xs text-surface-400">
                paid <Currency amount={b.paid_amount} />
              </div>
              <Tag tone={b.status === 'paid' ? 'success' : 'warn'}>{b.status}</Tag>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

function ApproveRejectSection({
  onApprove,
  approving,
  approveError,
  onReject,
  rejecting,
  rejectError,
}: {
  onApprove: () => void
  approving: boolean
  approveError: unknown
  onReject: (reason: string) => void
  rejecting: boolean
  rejectError: unknown
}) {
  const [showReject, setShowReject] = useState(false)
  const [reason, setReason] = useState('')

  return (
    <section className="space-y-3">
      <h4 className="text-xs uppercase tracking-wide text-surface-400">Action required</h4>
      {!showReject ? (
        <div className="flex gap-2">
          <Button onClick={onApprove} disabled={approving}>
            {approving ? 'Approving…' : 'Approve'}
          </Button>
          <Button variant="ghost" onClick={() => setShowReject(true)}>
            Reject…
          </Button>
        </div>
      ) : (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (reason.trim()) onReject(reason.trim())
          }}
        >
          <Field label="Reason">
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" variant="danger" disabled={rejecting || !reason.trim()}>
              {rejecting ? 'Rejecting…' : 'Confirm reject'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setShowReject(false)
                setReason('')
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
      {approveError ? <ErrorBanner error={approveError} /> : null}
      {rejectError ? <ErrorBanner error={rejectError} /> : null}
    </section>
  )
}

function RecordReceiptSection({
  batch,
  vendors,
  submitting,
  error,
  onSubmit,
}: {
  batch: LifecycleBatch
  vendors: { id: string; name: string }[]
  submitting: boolean
  error: unknown
  onSubmit: (input: {
    vendor_id?: string
    challan_number?: string
    challan_date?: number
    items?: ReceiptItemInput[]
  }) => void
}) {
  const [vendorId, setVendorId] = useState(batch.vendor_id ?? '')
  const [challanNumber, setChallanNumber] = useState('')
  const [challanDate, setChallanDate] = useState(() => todayIso())
  const pendingItems = batch.items.filter(
    (i) => i.lifecycle_status !== 'received' && i.lifecycle_status !== 'billed' && i.lifecycle_status !== 'paid',
  )
  const [qtyByItem, setQtyByItem] = useState<Record<string, string>>(() =>
    Object.fromEntries(pendingItems.map((i) => [i.id, String(i.quantity)])),
  )

  return (
    <section className="space-y-3">
      <h4 className="text-xs uppercase tracking-wide text-surface-400">Record receipt</h4>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          const items: ReceiptItemInput[] = pendingItems.map((i) => ({
            request_id: i.id,
            quantity_received: Number(qtyByItem[i.id] ?? i.quantity),
          }))
          onSubmit({
            vendor_id: vendorId || undefined,
            challan_number: challanNumber || undefined,
            challan_date: parseDateToUnix(challanDate),
            items,
          })
        }}
      >
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Vendor">
            <Select value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
              <option value="">Select…</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Challan #">
            <Input
              value={challanNumber}
              onChange={(e) => setChallanNumber(e.target.value)}
            />
          </Field>
          <Field label="Challan date">
            <Input
              type="date"
              value={challanDate}
              onChange={(e) => setChallanDate(e.target.value)}
            />
          </Field>
        </div>
        {pendingItems.length > 0 && (
          <div>
            <div className="text-xs text-surface-400 mb-1">Quantities received</div>
            <ul className="divide-y divide-surface-800 text-sm">
              {pendingItems.map((i) => (
                <li key={i.id} className="py-2 grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-7">
                    <div>{i.material_name}</div>
                    <div className="text-xs text-surface-400">
                      requested {i.quantity} {i.unit || i.material_unit}
                    </div>
                  </div>
                  <div className="col-span-5">
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={qtyByItem[i.id] ?? ''}
                      onChange={(e) =>
                        setQtyByItem((prev) => ({ ...prev, [i.id]: e.target.value }))
                      }
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        {error ? <ErrorBanner error={error} /> : null}
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Recording…' : 'Record receipt'}
        </Button>
      </form>
    </section>
  )
}

function CreateBillSection({
  batch,
  vendors,
  submitting,
  error,
  onSubmit,
}: {
  batch: LifecycleBatch
  vendors: { id: string; name: string }[]
  submitting: boolean
  error: unknown
  onSubmit: (input: {
    vendor_id: string
    bill_number: string
    bill_date: number
    gst_rate: '18'
    gst_type: 'intra'
    line_items: BillLineItemInput[]
  }) => void
}) {
  const [vendorId, setVendorId] = useState(batch.vendor_id ?? '')
  const [billNumber, setBillNumber] = useState('')
  const [billDate, setBillDate] = useState(() => todayIso())
  const [amounts, setAmounts] = useState<Record<string, string>>(() =>
    Object.fromEntries(batch.items.map((i) => [i.id, ''])),
  )

  return (
    <section className="space-y-3">
      <h4 className="text-xs uppercase tracking-wide text-surface-400">Create vendor bill</h4>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          if (!vendorId) return
          const line_items: BillLineItemInput[] = batch.items
            .map((i) => ({
              material_request_id: i.id,
              basic_amount: Number(amounts[i.id] ?? 0),
            }))
            .filter((l) => l.basic_amount > 0)
          if (!line_items.length) return
          onSubmit({
            vendor_id: vendorId,
            bill_number: billNumber,
            bill_date: parseDateToUnix(billDate),
            gst_rate: '18',
            gst_type: 'intra',
            line_items,
          })
        }}
      >
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Vendor">
            <Select
              value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
              required
            >
              <option value="">Select…</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Bill #">
            <Input
              value={billNumber}
              onChange={(e) => setBillNumber(e.target.value)}
              required
            />
          </Field>
          <Field label="Bill date">
            <Input
              type="date"
              value={billDate}
              onChange={(e) => setBillDate(e.target.value)}
              required
            />
          </Field>
        </div>
        <div>
          <div className="text-xs text-surface-400 mb-1">Line amounts (GST 18% intra)</div>
          <ul className="divide-y divide-surface-800 text-sm">
            {batch.items.map((i) => (
              <li key={i.id} className="py-2 grid grid-cols-12 gap-2 items-center">
                <div className="col-span-7">
                  <div>{i.material_name}</div>
                  <div className="text-xs text-surface-400">
                    {i.quantity} {i.unit || i.material_unit}
                  </div>
                </div>
                <div className="col-span-5">
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="basic amount"
                    value={amounts[i.id] ?? ''}
                    onChange={(e) =>
                      setAmounts((prev) => ({ ...prev, [i.id]: e.target.value }))
                    }
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
        {error ? <ErrorBanner error={error} /> : null}
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Creating…' : 'Create bill'}
        </Button>
      </form>
    </section>
  )
}

function PaySection({ vendorId, vendorName }: { vendorId: string; vendorName: string | null }) {
  return (
    <section className="space-y-2">
      <h4 className="text-xs uppercase tracking-wide text-surface-400">Pay</h4>
      <p className="text-sm text-surface-300">
        Bill is awaiting payment. Settle FIFO from the vendor page.
      </p>
      <Link
        to={`/vendors/${vendorId}`}
        className="inline-block rounded-md bg-accent-500 hover:bg-accent-600 px-3 py-1.5 text-sm font-medium text-white"
      >
        Open {vendorName ?? 'vendor'} →
      </Link>
    </section>
  )
}

function statusTone(status: string): 'default' | 'success' | 'warn' | 'danger' {
  const s = status.toLowerCase()
  if (s.includes('approved') || s.includes('completed') || s.includes('received') || s === 'paid')
    return 'success'
  if (s.includes('pending') || s.includes('draft') || s.includes('partial') || s === 'requested')
    return 'warn'
  if (s.includes('rejected') || s.includes('cancelled')) return 'danger'
  return 'default'
}

function todayIso(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function parseDateToUnix(iso: string): number {
  const ts = Date.parse(iso)
  return Number.isNaN(ts) ? Math.floor(Date.now() / 1000) : Math.floor(ts / 1000)
}
